<template>
  <div class="flow-list">
    <h3 class="label mt-2">{{ $t("objectNames.inflow", 3) }}</h3>
    <button-list
      :deletable="true"
      :items="inflowBranches"
      :selected-item="selectedFlowBranch"
      @delete="deleteFlowBranch"
      @select="selectFlowBranch($event)"
    >
      <template #default="{ item: flowBranch }">
        <span>
          {{ flowTitles[flowBranch.flowId] }}
        </span>
      </template>

      <template #after-list>
        <item-combo-select
          key="resource-menu-targetselect"
          :instruction="$t('texts.chooseOutgoingItems')"
          :available-ids="selectableFlowItemIds"
          :targets="targets"
          :resources="resources"
          :indices="indices"
          :hide-selected="true"
          :placeholder="`+ ${$t('labels.new')}`"
          :net-choosable="false"
          @select-single="addFlow($event, 'sink')"
        >
          <template #before-options="{ close }">
            <button
              type="button"
              class="options-list__option"
              @click="() => { addFlow(null, 'sink'); close() }"
            >
              <lucide-icon name="cloud" style="color: var(--color-text-light)" /> {{ $t("labels.outerSource") }}
            </button>
          </template>

          <template #null-text>
            <span style="font-style: italic">{{
              $t("labels.noEndElement")
            }}</span>
          </template>
        </item-combo-select>
      </template>
    </button-list>

    <h3 class="label">{{ $t("objectNames.outflow", 3) }}</h3>
    <button-list
      :deletable="true"
      :items="outflowBranches"
      :selected-item="selectedFlowBranch"
      @delete="deleteFlowBranch"
      @select="selectFlowBranch($event)"
    >
      <template #default="{ item: flowBranch }: { item: FlowBranchItem }">
        <span>
          {{ flowTitles[flowBranch.flowId] }}
        </span>
      </template>

      <template #after-list>
        <item-combo-select
          key="resource-menu-elementselect"
          :instruction="$t('texts.chooseOutgoingItems')"
          :available-ids="selectableFlowItemIds"
          :targets="targets"
          :resources="resources"
          :indices="indices"
          :hide-selected="true"
          :placeholder="`+ ${$t('labels.new')}`"
          :net-choosable="false"
          @select-single="addFlow($event, 'source')"
        >
          <template #before-options="{ close }">
            <button
              type="button"
              class="options-list__option"
              @click="() => { addFlow(null, 'source'); close() }"
            >
              <lucide-icon name="cloud" style="color: var(--color-text-light)" /> {{ $t("labels.outerSink") }}
            </button>
          </template>

          <template #null-text>
            <span style="font-style: italic">{{
              $t("labels.noEndElement")
            }}</span>
          </template>
        </item-combo-select>
      </template>
    </button-list>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { map } from "lodash";

import ButtonList from "@core/ui/ButtonList.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import ItemComboSelect from "@editor/ui/ItemComboSelect.vue";

import { useFlowStore } from "@editor/items/flow/flows.store";
import { useFlowBranchStore } from "@editor/items/flow/flowBranches.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "@editor/items/index/indices.store";

import type { FlowItem } from "@editor/items/flow/FlowItem";
import type { FlowBranchItem } from "@editor/items/flow/FlowBranchItem";

const props = defineProps<{
  elementType: "target" | "resource";
  elementId: number;
  subSelection?: { type: string; id: number } | null;
}>();

const emit = defineEmits<{
  (e: 'select', item: { type: 'flow'; id: number }): void;
}>();

const flowStore = useFlowStore();
const flowBranchStore = useFlowBranchStore();

const targets = computed(() => useTargetStore().items);
const resources = computed(() => useResourceStore().items);
const indices = computed(() => useIndexStore().items);

const element = computed(() => {
  if (props.elementType == "target") return targets.value[props.elementId];
  if (props.elementType == "resource") return resources.value[props.elementId];
  return null;
});

// ===================
// FLOWS

const flowBranches = computed(() => {
  if (element.value == null) return [];
  return flowBranchStore.byElementUid[element.value.uid] ?? [];
});

const inflowBranches = computed(() => {
  return flowBranches.value.filter((f) => f.branchType == "sink");
});
const outflowBranches = computed(() => {
  return flowBranches.value.filter((f) => f.branchType == "source");
});

const flowTitles = computed(() => flowStore.titles);

const selectedFlow = computed(() => {
  if (props.subSelection == null) return null;
  if (props.subSelection.type != "flow") return null;
  return flowStore.items[props.subSelection.id];
});

const selectedFlowBranch = computed(() => {
  return flowBranches.value.find((f) => f.flowId == selectedFlow.value?.id);
});

const selectableFlowItemIds = computed(() => {
  let targetIds = map(targets.value, (t) => t.id);
  let resourceIds = map(resources.value, (r) => r.id);

  if (props.elementType == "target") targetIds = targetIds.filter((t) => t != props.elementId);
  if (props.elementType == "resource") resourceIds = resourceIds.filter((r) => r != props.elementId);
  
  return {
    target: targetIds,
    resource: resourceIds,
    index: []
  };
});

async function addFlow(item: {
  type: "target" | "resource";
  id: number;
  title: string;
} | null, effectType: "source" | "sink") {
  let a = effectType == "source" ? element.value : item;
  let b = effectType == "source" ? item : element.value;

  const { flow } = await flowBranchStore.createFlowBetween(a, b);
  selectFlow(flow);
}

function deleteFlowBranch(flowBranch: FlowBranchItem) {
  flowBranchStore.delete(flowBranch);
}

function selectFlowBranch(flowBranch: FlowBranchItem) {
  emit("select", {
    type: "flow",
    id: flowBranch.flowId
  });
}

function selectFlow(flow: FlowItem) {
  emit("select", flow);
}
</script>

<style></style>